import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireAuth = true,
  redirectTo = '/login'
}) => {
  const { user, loading, session } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Log authentication state for debugging
    console.log('AuthGuard state:', {
      user: user?.id,
      session: !!session,
      loading,
      requireAuth,
      path: location.pathname
    });
  }, [user, session, loading, requireAuth, location.pathname]);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-purple"></div>
          <p className="text-gray-500">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuth && !user) {
    console.log('User not authenticated, redirecting to:', redirectTo);
    // Save the attempted location
    const from = location.pathname + location.search;
    return <Navigate to={redirectTo} state={{ from }} replace />;
  }

  // User is authenticated or auth not required
  return <>{children}</>;
};

export default AuthGuard;
